/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Multi-Format CAT Document Export Modal (Task 10.5.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  X,
  Download,
  FileText,
  Code,
  Table,
  Database,
  FileCheck2,
  Check,
  Sparkles,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import { useCatEditorStore } from '../../stores/useCatEditorStore';
import {
  generateXliff12,
  generateTmx,
  generateBilingualHtml,
  generateCsvExport,
  generateTargetDocumentText,
  triggerFileDownload,
} from '../../utils/cat-export-helper';

export type ExportFormatType = 'docx' | 'html' | 'csv' | 'xlf' | 'tmx' | 'txt';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ExportModal: React.FC<ExportModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { metadata, project, segments } = useCatEditorStore();

  const [selectedFormat, setSelectedFormat] = useState<ExportFormatType>('docx');
  const [includeUntranslated, setIncludeUntranslated] = useState(true);
  const [includeQa, setIncludeQa] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);

  if (!isOpen) return null;

  const totalSegments = segments.length;
  const translatedSegments = segments.filter(
    (s) => s.targetText && s.targetText.trim().length > 0
  ).length;

  const formats: Array<{
    type: ExportFormatType;
    icon: React.ComponentType<{ className?: string }>;
    titleKey: string;
    descKey: string;
    extension: string;
    badge: string;
  }> = [
    {
      type: 'docx',
      icon: FileText,
      titleKey: 'cat.exportFormatDocxTitle',
      descKey: 'cat.exportFormatDocxDesc',
      extension: '.docx',
      badge: 'Target Doc',
    },
    {
      type: 'html',
      icon: Table,
      titleKey: 'cat.exportFormatHtmlTitle',
      descKey: 'cat.exportFormatHtmlDesc',
      extension: '.html',
      badge: 'Bilingual Review',
    },
    {
      type: 'csv',
      icon: Table,
      titleKey: 'cat.exportFormatCsvTitle',
      descKey: 'cat.exportFormatCsvDesc',
      extension: '.csv',
      badge: 'Excel UTF-8',
    },
    {
      type: 'xlf',
      icon: Code,
      titleKey: 'cat.exportFormatXlfTitle',
      descKey: 'cat.exportFormatXlfDesc',
      extension: '.xlf',
      badge: 'XLIFF 1.2',
    },
    {
      type: 'tmx',
      icon: Database,
      titleKey: 'cat.exportFormatTmxTitle',
      descKey: 'cat.exportFormatTmxDesc',
      extension: '.tmx',
      badge: 'TM Exchange',
    },
    {
      type: 'txt',
      icon: FileCheck2,
      titleKey: 'cat.exportFormatTxtTitle',
      descKey: 'cat.exportFormatTxtDesc',
      extension: '.txt',
      badge: 'Clean Text',
    },
  ];

  const handleExecuteExport = () => {
    setIsExporting(true);
    setExportSuccess(false);

    const baseName = (metadata.documentName || project.title || 'translation_export').replace(
      /\.[^/.]+$/,
      ''
    );
    const exportMeta = {
      title: project.title || metadata.documentName,
      sourceLang: metadata.sourceLang || project.sourceLang || 'en',
      targetLang: metadata.targetLang || project.targetLang || 'vi',
      fileName: metadata.documentName,
      domain: metadata.domain || project.domain,
    };

    setTimeout(() => {
      try {
        switch (selectedFormat) {
          case 'docx': {
            // Reassembled text output formatted for document
            const textContent = generateTargetDocumentText(segments, includeUntranslated);
            triggerFileDownload(
              textContent,
              `${baseName}_${exportMeta.targetLang}.txt`,
              'text/plain;charset=utf-8'
            );
            break;
          }
          case 'html': {
            const htmlContent = generateBilingualHtml(exportMeta, segments, {
              includeQa,
              includeUntranslated,
            });
            triggerFileDownload(
              htmlContent,
              `${baseName}_bilingual_report.html`,
              'text/html;charset=utf-8'
            );
            break;
          }
          case 'csv': {
            const csvContent = generateCsvExport(segments, { includeUntranslated });
            triggerFileDownload(
              csvContent,
              `${baseName}_bilingual_data.csv`,
              'text/csv;charset=utf-8'
            );
            break;
          }
          case 'xlf': {
            const xliffContent = generateXliff12(exportMeta, segments, { includeUntranslated });
            triggerFileDownload(
              xliffContent,
              `${baseName}.xlf`,
              'application/x-xliff+xml;charset=utf-8'
            );
            break;
          }
          case 'tmx': {
            const tmxContent = generateTmx(exportMeta, segments);
            triggerFileDownload(
              tmxContent,
              `${baseName}_ledger.tmx`,
              'application/x-tmx+xml;charset=utf-8'
            );
            break;
          }
          case 'txt': {
            const plainText = generateTargetDocumentText(segments, includeUntranslated);
            triggerFileDownload(
              plainText,
              `${baseName}_target.txt`,
              'text/plain;charset=utf-8'
            );
            break;
          }
        }

        setExportSuccess(true);
        setTimeout(() => {
          setIsExporting(false);
          onClose();
        }, 1200);
      } catch (err) {
        console.error('Export generation error:', err);
        setIsExporting(false);
      }
    }, 400);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-2xl bg-surface border border-surface-border rounded-xl shadow-2xl flex flex-col overflow-hidden max-h-[90vh]">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-5 border-b border-surface-border bg-surface-elevated/40">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-primary/10 text-primary border border-primary/20">
              <Download className="size-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-foreground">
                {t('cat.exportModalTitle')}
              </h2>
              <p className="text-xs text-foreground-muted">
                {metadata.documentName || project.title} &bull; {translatedSegments}/{totalSegments} {t('cat.segmentsCountLabel')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-foreground-muted hover:text-foreground rounded-lg hover:bg-surface-elevated transition-colors"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Format Selection Grid */}
          <div>
            <label className="block text-xs font-semibold text-foreground-muted uppercase tracking-wider mb-2.5">
              {t('cat.selectFormatLabel')}
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {formats.map((fmt) => {
                const Icon = fmt.icon;
                const isSelected = selectedFormat === fmt.type;
                return (
                  <button
                    key={fmt.type}
                    type="button"
                    onClick={() => setSelectedFormat(fmt.type)}
                    className={`flex items-start gap-3 p-3 text-left rounded-lg border transition-all ${
                      isSelected
                        ? 'bg-primary/5 border-primary ring-1 ring-primary/30 shadow-sm'
                        : 'bg-surface-elevated/40 border-surface-border hover:border-surface-border/80'
                    }`}
                  >
                    <div
                      className={`p-2 rounded-lg mt-0.5 shrink-0 ${
                        isSelected
                          ? 'bg-primary text-primary-fg'
                          : 'bg-surface-elevated text-foreground-muted'
                      }`}
                    >
                      <Icon className="size-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1">
                        <span className="text-xs font-bold text-foreground truncate">
                          {t(fmt.titleKey)}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-mono font-semibold bg-surface-elevated text-foreground-muted shrink-0">
                          {fmt.badge}
                        </span>
                      </div>
                      <p className="text-[11px] text-foreground-muted line-clamp-2 mt-0.5">
                        {t(fmt.descKey)}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Export Options */}
          <div className="p-3.5 rounded-lg bg-surface-elevated/40 border border-surface-border space-y-3">
            <label className="block text-xs font-semibold text-foreground-muted uppercase tracking-wider">
              {t('cat.exportOptionsLabel')}
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-2.5 text-xs text-foreground cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={includeUntranslated}
                  onChange={(e) => setIncludeUntranslated(e.target.checked)}
                  className="rounded border-surface-border text-primary focus:ring-primary/20"
                />
                <span>{t('cat.optionIncludeUntranslated')}</span>
              </label>

              {(selectedFormat === 'html' || selectedFormat === 'csv') && (
                <label className="flex items-center gap-2.5 text-xs text-foreground cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={includeQa}
                    onChange={(e) => setIncludeQa(e.target.checked)}
                    className="rounded border-surface-border text-primary focus:ring-primary/20"
                  />
                  <span>{t('cat.optionIncludeQa')}</span>
                </label>
              )}
            </div>
          </div>

          {/* Zero-Egress Storage Notice */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/5 border border-primary/10 text-xs text-foreground-muted">
            <Sparkles className="size-4 text-primary shrink-0" />
            <span>{t('cat.zeroEgressNotice')}</span>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-2.5 p-4 border-t border-surface-border bg-surface-elevated/40">
          <button
            type="button"
            onClick={onClose}
            disabled={isExporting}
            className="px-4 py-2 text-xs font-medium rounded-lg text-foreground-muted hover:text-foreground hover:bg-surface-elevated transition-colors"
          >
            {t('cat.closeModal')}
          </button>
          <button
            type="button"
            onClick={handleExecuteExport}
            disabled={isExporting}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg bg-primary text-primary-fg hover:bg-primary/90 shadow-sm transition-colors disabled:opacity-50"
          >
            {isExporting ? (
              <>
                <div className="size-3.5 border-2 border-primary-fg/30 border-t-primary-fg rounded-full animate-spin" />
                <span>{t('cat.generatingExport')}</span>
              </>
            ) : exportSuccess ? (
              <>
                <Check className="size-3.5" />
                <span>{t('cat.downloadReady')}</span>
              </>
            ) : (
              <>
                <Download className="size-3.5" />
                <span>{t('cat.downloadExportBtn')}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
